import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useOutletContext } from 'react-router';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	TextField,
	MenuItem,
	Alert,
	Box,
	Typography,
	Tooltip,
	IconButton,
	Autocomplete,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import InfoIcon from '@mui/icons-material/Info';
import { authedApiFetch } from 'utils/Api';

// Hardcoded role options for this internal functionality since it's rarely changed
const ROLE_OPTIONS = [
	{ value: 'altvia_admin', label: 'Altvia Admin' },
	{ value: 'altvia_power_user', label: 'Altvia Power User' },
	{ value: 'altvia_user', label: 'Altvia User' },
	{ value: 'tenant_admin', label: 'Tenant Admin' },
	{ value: 'tenant_user', label: 'Tenant User' },
	{ value: 'tenant_read_only', label: 'Tenant Read Only' },
];

const TENANT_ROLES = ['tenant_admin', 'tenant_user', 'tenant_read_only'];

const CreateUserModal = ({
	tenantId,
	open,
	onClose,
	showNotification,
	onSuccess,
}) => {
	const outletData = useOutletContext();
	const tenants = outletData?.tenants || [];

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});

	// Form state
	const [formData, setFormData] = useState({
		email: '',
		first_name: '',
		last_name: '',
		role: '',
		tenant_id: '',
		sf_username_in_client_org: '',
		sf_user_id_in_client_org: '',
	});

	// Reset form when the modal closes
	useEffect(() => {
		if (!open) {
			setFormData({
				email: '',
				first_name: '',
				last_name: '',
				role: '',
				tenant_id: '',
				sf_username_in_client_org: '',
				sf_user_id_in_client_org: '',
			});
			setErrors({});
		}
	}, [open]);

	const handleInputChange = (field) => (event) => {
		const value = event.target.value;
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear error for this field when user starts typing
		if (errors[field]) {
			clearFieldErrors(field);
		}

		// If an altvia role is selected, clear the tenant_id
		if (field === 'role' && !TENANT_ROLES.includes(value)) {
			setFormData((prev) => ({
				...prev,
				tenant_id: '',
			}));
			clearFieldErrors('tenant_id');
		}

		// If a tenant role is selected and the tenant_id is empty, pre-fill the tenant_id to current tenant
		if (
			field === 'role' &&
			TENANT_ROLES.includes(value) &&
			!formData.tenant_id
		) {
			setFormData((prev) => ({
				...prev,
				tenant_id: tenantId || '',
			}));
			clearFieldErrors('tenant_id');
		}
	};

	const handleTenantChange = (_event, value) => {
		if (value) {
			setFormData((prev) => ({
				...prev,
				tenant_id: value.id,
			}));

			clearFieldErrors('tenant_id');
		}
	};

	const clearFieldErrors = (field) => {
		setErrors((prev) => ({
			...prev,
			[field]: '',
		}));
	};

	const isValidEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleValidateEmail = (email) => {
		if (email.trim() === '') {
			return;
		}

		const isValid = isValidEmail(email);
		const newErrors = {};
		if (!isValid) {
			newErrors.email = 'Invalid email format';
		}

		setErrors(newErrors);
	};

	const isValidForm = () => {
		const newErrors = {};

		// Required fields
		if (!formData.email.trim()) {
			newErrors.email = 'Email is required';
		} else if (!isValidEmail(formData.email)) {
			newErrors.email = 'Invalid email format';
		}

		if (!formData.first_name.trim()) {
			newErrors.first_name = 'First name is required';
		}

		if (!formData.last_name.trim()) {
			newErrors.last_name = 'Last name is required';
		}

		if (!formData.role) {
			newErrors.role = 'Role is required';
		}

		// Tenant ID required for tenant roles
		if (TENANT_ROLES.includes(formData.role) && !formData.tenant_id.trim()) {
			newErrors.tenant_id = 'Tenant ID is required for tenant roles';
		}

		if (!formData.sf_username_in_client_org.trim()) {
			newErrors.sf_username_in_client_org = 'Salesforce username is required';
		}

		if (!formData.sf_user_id_in_client_org.trim()) {
			newErrors.sf_user_id_in_client_org = 'Salesforce user ID is required';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const buildPayload = (formData) => {
		const payload = {
			email: formData.email.trim(),
			first_name: formData.first_name.trim(),
			last_name: formData.last_name.trim(),
			role: formData.role,
		};

		// Add optional fields if provided
		if (TENANT_ROLES.includes(formData.role) && formData.tenant_id.trim()) {
			payload.tenant_id = formData.tenant_id.trim();
		}

		if (formData.sf_username_in_client_org.trim()) {
			payload.sf_username_in_client_org =
				formData.sf_username_in_client_org.trim();
		}

		if (formData.sf_user_id_in_client_org.trim()) {
			payload.sf_user_id_in_client_org =
				formData.sf_user_id_in_client_org.trim();
		}

		return payload;
	};

	const handleSuccessNotification = (user) => {
		let successMessage = '';
		let userMessage = `The user "${user.email}" is created successfully.`;

		if (user.skip_cognito) {
			successMessage = `${userMessage} User can now sign in using Google auth.`;
		} else {
			successMessage = `${userMessage} A temporary password has been sent to the user's email for initial login.`;
		}

		showNotification(successMessage, 'success');
		onSuccess();
	};

	const handleClose = () => {
		if (!loading) {
			onClose();
		}
	};

	const handleSubmit = async () => {
		if (!isValidForm()) {
			return;
		}

		setLoading(true);

		try {
			const payload = buildPayload(formData);

			const user = await authedApiFetch({
				endpoint: '/user_login/create_test_user',
				method: 'POST',
				payload: { user: payload },
			});

			handleSuccessNotification(user);
			onClose();
		} catch (error) {
			let errorMessage = `Failed to create user. ${error.message}.`;
			console.error(errorMessage);
			showNotification(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	};

	const isTenantRole = (role) => TENANT_ROLES.includes(role);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth='sm'
			fullWidth
			scroll='paper'
		>
			<DialogTitle>Create User</DialogTitle>

			<DialogContent dividers>
				<Alert severity='info' sx={{ mb: 3 }}>
					<Typography variant='body2'>
						<strong>Important:</strong> Please ensure the Salesforce user is
						already created so that the Salesforce Username and User ID can be
						populated below.
					</Typography>
				</Alert>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					{/* Role Selector */}
					<TextField
						select
						label='Role'
						value={formData.role}
						onChange={handleInputChange('role')}
						error={!!errors.role}
						helperText={errors.role}
						required
						fullWidth
						disabled={loading}
					>
						{ROLE_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>

					{/* Tenant Search Selector */}
					<Box
						sx={{
							opacity: !isTenantRole(formData.role) || loading ? 0.5 : 1,
							pointerEvents:
								!isTenantRole(formData.role) || loading ? 'none' : 'auto',
						}}
					>
						<Autocomplete
							options={tenants}
							getOptionLabel={(option) => option.name}
							value={
								tenants.find(
									(t) => String(t.id) === String(formData.tenant_id)
								) || null
							}
							onChange={handleTenantChange}
							renderInput={(params) => (
								<TextField
									{...params}
									label='Tenant (only required for tenant roles)'
									error={!!errors.tenant_id}
								/>
							)}
							componentsProps={{
								popper: {
									placement: 'bottom-start',
									modifiers: [
										{
											name: 'flip',
											enabled: false,
										},
										{
											name: 'offset',
											options: {
												offset: [0, 8],
											},
										},
									],
								},
								paper: {
									sx: {
										borderRadius: 0,
									},
								},
							}}
							ListboxProps={{
								sx: {
									maxHeight: 300,
								},
							}}
							fullWidth
						/>
						{errors.tenant_id && (
							<Typography
								variant='caption'
								color='error'
								sx={{ mt: 0.5, ml: 2 }}
							>
								{errors.tenant_id}
							</Typography>
						)}
					</Box>

					{/* Email Input */}
					<TextField
						label='Email'
						type='email'
						value={formData.email}
						onChange={handleInputChange('email')}
						onBlur={(e) => handleValidateEmail(e.target.value)}
						error={!!errors.email}
						helperText={errors.email}
						required
						fullWidth
						disabled={loading}
						InputProps={{
							endAdornment: (
								<Tooltip
									title='Use the main gmail work email (e.g., john@altvia.com) for the "altvia_admin" role and then sign in through google auth. For other roles, use the email alias (e.g., john+test@altvia.com) to receive a temporary password for initial login.'
									placement='top'
									arrow
								>
									<IconButton size='small' edge='end'>
										<InfoIcon fontSize='small' color='action' />
									</IconButton>
								</Tooltip>
							),
						}}
					/>

					{/* First Name Input */}
					<TextField
						label='First Name'
						value={formData.first_name}
						onChange={handleInputChange('first_name')}
						error={!!errors.first_name}
						helperText={errors.first_name}
						required
						fullWidth
						disabled={loading}
					/>

					{/* Last Name Input */}
					<TextField
						label='Last Name'
						value={formData.last_name}
						onChange={handleInputChange('last_name')}
						error={!!errors.last_name}
						helperText={errors.last_name}
						required
						fullWidth
						disabled={loading}
					/>

					{/* Salesforce Username Input */}
					<TextField
						label='Salesforce Username'
						value={formData.sf_username_in_client_org}
						onChange={handleInputChange('sf_username_in_client_org')}
						error={!!errors.sf_username_in_client_org}
						helperText={errors.sf_username_in_client_org}
						required
						disabled={loading}
						fullWidth
					/>

					{/* Salesforce User ID Input */}
					<TextField
						label='Salesforce User ID'
						value={formData.sf_user_id_in_client_org}
						onChange={handleInputChange('sf_user_id_in_client_org')}
						error={!!errors.sf_user_id_in_client_org}
						helperText={errors.sf_user_id_in_client_org}
						required
						disabled={loading}
						fullWidth
					/>
				</Box>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose} variant='contained' disabled={loading}>
					Cancel
				</Button>

				<LoadingButton
					key={`create-user-button`}
					variant='contained'
					color='primary'
					loading={loading}
					disabled={loading}
					onClick={handleSubmit}
				>
					Create User
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

CreateUserModal.propTypes = {
	tenantId: PropTypes.string,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	showNotification: PropTypes.func.isRequired,
	onSuccess: PropTypes.func.isRequired,
};

export default CreateUserModal;
